import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Target,
  Plus,
  TrendingUp,
  TrendingDown,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Pause,
  Play,
  Trash2,
  Brain,
  BarChart3,
  Users,
  Globe,
  Linkedin,
  MousePointerClick,
  DollarSign,
  RefreshCw,
  ChevronRight,
  Info,
  Clock,
  Search,
  Zap,
  Share2,
  Mail,
  FileText,
  Eye,
  ArrowDownRight,
  Percent,
  Link2,
  Phone,
  Briefcase,
  CreditCard,
  Award,
  ThumbsUp,
} from "lucide-react";

// Goal templates for the Goal Library
// Status: ✅ READY, ⚠️ NEEDS_FIX, 🔧 NEEDS_INTEGRATION, 📝 SPECIAL_PAGE
const GOAL_TEMPLATES = [
  // === LEADS (✅ All Ready) ===
  {
    id: "mqls",
    name: "Marketing Qualified Leads",
    description: "Track total MQLs generated",
    icon: Users,
    category: "leads",
    metrics: ["daily_mqls"],
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "HubSpot Contacts",
  },
  {
    id: "sqls",
    name: "Sales Qualified Leads",
    description: "Track SQLs from marketing",
    icon: Target,
    category: "leads",
    metrics: ["daily_sqls"],
    color: "text-green-600",
    bgColor: "bg-green-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "HubSpot Contacts",
  },
  {
    id: "mql_to_sql_ratio",
    name: "MQL to SQL Conversion Rate",
    description: "Track how many MQLs convert to SQLs",
    icon: Percent,
    category: "leads",
    metrics: ["mql_to_sql_rate"],
    unit: "percent",
    color: "text-indigo-600",
    bgColor: "bg-indigo-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "Calculated",
  },
  {
    id: "lead_response_time",
    name: "Lead Response Time",
    description: "Average time to first contact with new leads",
    icon: Clock,
    category: "leads",
    metrics: ["avg_response_time"],
    unit: "hours",
    color: "text-rose-600",
    bgColor: "bg-rose-100",
    supportsPercentChange: true,
    lowerIsBetter: true,
    status: "needs_integration",
    dataSource: "HubSpot (hs_time_to_first_reply)",
    note: "Requires adding engagement data to sync",
  },
  
  // === TRAFFIC (HubSpot > GA4) ===
  {
    id: "website_sessions",
    name: "Website Sessions",
    description: "Total website sessions",
    icon: Globe,
    category: "traffic",
    metrics: ["website_sessions"],
    color: "text-cyan-600",
    bgColor: "bg-cyan-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "HubSpot / GA4",
    note: "Uses HubSpot if available, falls back to GA4",
    showDataSourceIndicator: true,
  },
  {
    id: "organic_traffic",
    name: "Organic Traffic",
    description: "Combined: Organic Search + Organic Social + AI Referrals",
    icon: TrendingUp,
    category: "traffic",
    metrics: ["organic_search", "organic_social", "ai_referral"],
    isCombined: true,
    breakdown: [
      { id: "organic_search", name: "Organic Search", icon: Search },
      { id: "organic_social", name: "Organic Social", icon: Share2 },
      { id: "ai_referral", name: "AI Referrals", icon: Zap },
    ],
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
    supportsPercentChange: true,
    status: "needs_integration",
    dataSource: "Google Analytics 4",
  },
  {
    id: "bounce_rate",
    name: "Bounce Rate Reduction",
    description: "Track and reduce single-page sessions",
    icon: ArrowDownRight,
    category: "traffic",
    metrics: ["bounce_rate"],
    unit: "percent",
    color: "text-red-600",
    bgColor: "bg-red-100",
    supportsPercentChange: true,
    lowerIsBetter: true,
    status: "needs_integration",
    dataSource: "Google Analytics 4",
  },
  {
    id: "pages_per_session",
    name: "Pages Per Session",
    description: "Average pages viewed per visit",
    icon: FileText,
    category: "traffic",
    metrics: ["pages_per_session"],
    color: "text-sky-600",
    bgColor: "bg-sky-100",
    supportsPercentChange: true,
    status: "needs_integration",
    dataSource: "Google Analytics 4",
  },
  
  // === ENGAGEMENT ===
  {
    id: "dwell_time",
    name: "Avg. Session Duration",
    description: "How long visitors stay on site",
    icon: Clock,
    category: "engagement",
    metrics: ["avg_session_duration"],
    unit: "seconds",
    drillDown: {
      enabled: true,
      dimensions: ["page", "source", "device"],
      description: "View by page, traffic source, or device",
    },
    color: "text-violet-600",
    bgColor: "bg-violet-100",
    supportsPercentChange: true,
    status: "needs_integration",
    dataSource: "Google Analytics 4",
  },
  {
    id: "email_engagement",
    name: "Email Engagement Rate",
    description: "Open rates and click-through rates",
    icon: Mail,
    category: "engagement",
    metrics: ["email_open_rate", "email_ctr"],
    unit: "percent",
    color: "text-amber-600",
    bgColor: "bg-amber-100",
    supportsPercentChange: true,
    status: "needs_integration",
    dataSource: "HubSpot Marketing Hub",
    note: "Requires Marketing Hub subscription",
  },
  
  // === CONVERSIONS ===
  {
    id: "form_submissions",
    name: "Form Submissions",
    description: "Total form conversions",
    icon: MousePointerClick,
    category: "conversions",
    metrics: ["form_submissions"],
    color: "text-purple-600",
    bgColor: "bg-purple-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "HubSpot Forms",
  },
  {
    id: "demo_requests",
    name: "Demo Requests",
    description: "Demo/meeting requests from website",
    icon: Phone,
    category: "conversions",
    metrics: ["demo_requests"],
    color: "text-fuchsia-600",
    bgColor: "bg-fuchsia-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "HubSpot Forms (filtered)",
    note: "Uses forms tagged as demo requests",
  },
  {
    id: "content_downloads",
    name: "Content Downloads",
    description: "Whitepapers, ebooks, resource downloads",
    icon: FileText,
    category: "conversions",
    metrics: ["content_downloads"],
    color: "text-lime-600",
    bgColor: "bg-lime-100",
    supportsPercentChange: true,
    status: "needs_config",
    dataSource: "HubSpot Forms / GA4 Events",
    note: "Requires selecting which pages/forms to track",
    opensPage: "/app/goals/content-tracking",
  },
  
  // === SOCIAL (✅ Ready) ===
  {
    id: "linkedin_followers",
    name: "LinkedIn Follower Growth",
    description: "New LinkedIn followers",
    icon: Linkedin,
    category: "social",
    metrics: ["linkedin_followers"],
    color: "text-indigo-600",
    bgColor: "bg-indigo-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "LinkedIn API",
  },
  {
    id: "linkedin_engagement",
    name: "LinkedIn Engagement Rate",
    description: "Likes, comments, shares per post",
    icon: ThumbsUp,
    category: "social",
    metrics: ["linkedin_engagement_rate"],
    unit: "percent",
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "LinkedIn API",
  },
  {
    id: "social_impressions",
    name: "Social Media Impressions",
    description: "Total reach across social platforms",
    icon: Eye,
    category: "social",
    metrics: ["social_impressions"],
    color: "text-cyan-600",
    bgColor: "bg-cyan-100",
    supportsPercentChange: true,
    status: "ready",
    dataSource: "LinkedIn API",
  },
  
  // === REVENUE (⚠️ Needs Fixes) ===
  {
    id: "deal_revenue",
    name: "Closed Won Revenue",
    description: "Revenue from closed won deals only",
    icon: DollarSign,
    category: "revenue",
    metrics: ["deal_revenue"],
    color: "text-amber-600",
    bgColor: "bg-amber-100",
    supportsPercentChange: true,
    status: "needs_fix",
    dataSource: "HubSpot Deals",
    note: "Filters by tenant's Closed Won stages",
  },
  {
    id: "pipeline_value",
    name: "Open Pipeline Value",
    description: "Total value of OPEN deals in pipeline",
    icon: Briefcase,
    category: "revenue",
    metrics: ["pipeline_value"],
    color: "text-green-600",
    bgColor: "bg-green-100",
    supportsPercentChange: true,
    status: "needs_fix",
    dataSource: "HubSpot Deals",
    note: "Excludes closed won/lost deals",
  },
  {
    id: "average_deal_size",
    name: "Average Deal Size",
    description: "Mean value of closed won deals",
    icon: CreditCard,
    category: "revenue",
    metrics: ["avg_deal_size"],
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
    supportsPercentChange: true,
    status: "needs_fix",
    dataSource: "HubSpot Deals",
  },
  {
    id: "win_rate",
    name: "Win Rate",
    description: "% of deals closed won vs. total closed",
    icon: Award,
    category: "revenue",
    metrics: ["win_rate"],
    unit: "percent",
    color: "text-yellow-600",
    bgColor: "bg-yellow-100",
    supportsPercentChange: true,
    status: "needs_fix",
    dataSource: "HubSpot Deals",
    note: "Won / (Won + Lost)",
  },
  {
    id: "sales_cycle_length",
    name: "Sales Cycle Length",
    description: "Average days from MQL to Closed Won",
    icon: Calendar,
    category: "revenue",
    metrics: ["sales_cycle_days"],
    unit: "days",
    color: "text-rose-600",
    bgColor: "bg-rose-100",
    supportsPercentChange: true,
    lowerIsBetter: true,
    status: "ready",
    dataSource: "HubSpot Deals",
    note: "Uses mql_date to closedate",
  },
  
  // === SEO (📝 Special Pages) ===
  {
    id: "gsc_top_queries",
    name: "Top 10 Search Terms",
    description: "Track GSC top organic queries (position < 11)",
    icon: Search,
    category: "seo",
    metrics: ["gsc_clicks", "gsc_impressions", "gsc_position"],
    drillDown: {
      enabled: true,
      dimensions: ["query", "page", "device", "country"],
      description: "View by query, landing page, device, or country",
    },
    color: "text-orange-600",
    bgColor: "bg-orange-100",
    supportsPercentChange: true,
    status: "needs_integration",
    dataSource: "Google Search Console",
    note: "Quarterly tracking only - SEO requires longer measurement periods",
    opensPage: "/app/goals/gsc-tracking",
    quarterlyOnly: true, // GSC goals should only be quarterly
  },
  {
    id: "organic_ctr",
    name: "Organic CTR",
    description: "Click-through rate from search results",
    icon: Link2,
    category: "seo",
    metrics: ["organic_ctr"],
    unit: "percent",
    color: "text-green-600",
    bgColor: "bg-green-100",
    supportsPercentChange: true,
    status: "needs_integration",
    dataSource: "Google Search Console",
  },
  
  // === COST EFFICIENCY (📝 Special Pages) ===
  {
    id: "cost_per_lead",
    name: "Cost Per Lead (CPL)",
    description: "Marketing spend ÷ leads generated",
    icon: DollarSign,
    category: "efficiency",
    metrics: ["cost_per_lead"],
    color: "text-red-600",
    bgColor: "bg-red-100",
    supportsPercentChange: true,
    lowerIsBetter: true,
    status: "needs_config",
    dataSource: "Manual + HubSpot",
    note: "Requires marketing spend entry",
  },
  {
    id: "cost_per_mql",
    name: "Cost Per MQL",
    description: "Marketing spend ÷ MQLs",
    icon: Users,
    category: "efficiency",
    metrics: ["cost_per_mql"],
    color: "text-orange-600",
    bgColor: "bg-orange-100",
    supportsPercentChange: true,
    lowerIsBetter: true,
    status: "needs_config",
    dataSource: "Manual + HubSpot",
  },
  {
    id: "marketing_roi",
    name: "Marketing ROI",
    description: "Revenue ÷ marketing spend (incl. labor)",
    icon: TrendingUp,
    category: "efficiency",
    metrics: ["marketing_roi"],
    unit: "percent",
    color: "text-green-600",
    bgColor: "bg-green-100",
    supportsPercentChange: true,
    status: "needs_config",
    dataSource: "HubSpot + Manual",
    note: "Configurable: spend + optional labor costs",
    opensPage: "/app/goals/marketing-roi",
  },
];

const PERIOD_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

const TARGET_TYPE_OPTIONS = [
  { value: "absolute", label: "Absolute Number", description: "e.g., 100 MQLs" },
  { value: "percent_increase", label: "% Increase", description: "e.g., +15% growth" },
  { value: "percent_decrease", label: "% Decrease", description: "e.g., -10% reduction (for costs)" },
];

const GoalsPage = () => {
  const { sessionToken, isAdmin, isSuperAdmin, user } = useAuth();
  const { pageTitles } = useBranding();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newGoal, setNewGoal] = useState({
    metric: "",
    target_value: "",
    target_type: "absolute",
    period: "monthly",
  });
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  
  // Baseline data for the selected template/period
  const [baselineData, setBaselineData] = useState(null);
  const [loadingBaseline, setLoadingBaseline] = useState(false);
  
  // AI Insights state (read-only - management is in Platform Owner Portal)
  const [aiInsights, setAiInsights] = useState({});
  const [loadingInsights, setLoadingInsights] = useState({});
  
  // Get current tenant ID
  const currentTenantId = sessionStorage.getItem("viewing_tenant_id");

  // Fetch baseline when template or period changes
  const fetchBaseline = useCallback(async (metricId, period) => {
    if (!sessionToken || !metricId) return;
    
    setLoadingBaseline(true);
    try {
      const params = { 
        Authorization: `Bearer ${sessionToken}`,
        metric: metricId,
        period: period
      };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      const response = await axios.get(`${API}/ai-analytics/metric-baseline`, { params });
      setBaselineData(response.data);
    } catch (error) {
      console.error("Error fetching baseline:", error);
      setBaselineData(null);
    } finally {
      setLoadingBaseline(false);
    }
  }, [sessionToken, currentTenantId]);

  // Load saved insight for a goal (GET request - only shows approved insights)
  const loadSavedInsight = useCallback(async (goalId) => {
    if (!sessionToken) return;
    
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      const response = await axios.get(
        `${API}/ai-analytics/goals/${goalId}/insights`,
        { params }
      );
      
      if (response.data.insight) {
        setAiInsights(prev => ({ ...prev, [goalId]: response.data }));
      }
    } catch (error) {
      console.error("Error loading saved insight:", error);
    }
  }, [sessionToken, currentTenantId]);

  // Load saved insights for at-risk goals on mount
  useEffect(() => {
    if (goals.length > 0 && sessionToken) {
      const atRiskGoals = goals.filter(g => 
        g.status === "active" && calcProgress(g.current_value, g.target_value) < 75
      );
      atRiskGoals.forEach(goal => {
        loadSavedInsight(goal.goal_id || goal.id);
      });
    }
  }, [goals, sessionToken, loadSavedInsight]);

  // When template or period changes, fetch baseline
  useEffect(() => {
    if (selectedTemplate && newGoal.period) {
      fetchBaseline(selectedTemplate.id, newGoal.period);
    }
  }, [selectedTemplate, newGoal.period, fetchBaseline]);

  // Calculate actual target from percent change
  const calculateActualTarget = () => {
    if (!baselineData?.baseline_value || !newGoal.target_value) return null;
    
    const baseline = baselineData.baseline_value;
    const inputValue = parseFloat(newGoal.target_value);
    
    if (newGoal.target_type === "absolute") {
      return inputValue;
    } else if (newGoal.target_type === "percent_increase") {
      // If user enters 110, interpret as 10% growth (110% of baseline)
      // If user enters 10, interpret as 10% growth
      const growthPercent = inputValue > 100 ? inputValue - 100 : inputValue;
      return Math.round(baseline * (1 + growthPercent / 100));
    } else if (newGoal.target_type === "percent_decrease") {
      const decreasePercent = inputValue > 100 ? inputValue - 100 : inputValue;
      return Math.round(baseline * (1 - decreasePercent / 100));
    }
    return null;
  };

  // Fetch goals
  const fetchGoals = useCallback(async (showRefreshToast = false) => {
    if (!sessionToken) return;
    
    setLoading(true);
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      // First refresh goal values from database
      try {
        if (showRefreshToast) {
          toast.loading("Calculating goal progress...", { id: "goal-refresh" });
        }
        const refreshResponse = await axios.post(`${API}/ai-analytics/goals/refresh`, {}, { params });
        if (showRefreshToast && refreshResponse.data.updated_count > 0) {
          toast.success(`Updated ${refreshResponse.data.updated_count} goal(s)`, { id: "goal-refresh" });
        } else if (showRefreshToast) {
          toast.dismiss("goal-refresh");
        }
      } catch (refreshErr) {
        console.log("Goal refresh skipped:", refreshErr.message);
        if (showRefreshToast) toast.dismiss("goal-refresh");
      }
      
      // Then fetch the updated goals
      const response = await axios.get(`${API}/ai-analytics/goals`, { params });
      setGoals(response.data.goals || []);
    } catch (error) {
      console.error("Error fetching goals:", error);
      toast.error("Failed to load goals");
    } finally {
      setLoading(false);
    }
  }, [sessionToken, currentTenantId]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // Create goal
  const handleCreateGoal = async () => {
    if (!newGoal.metric || !newGoal.target_value) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setCreating(true);
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      await axios.post(
        `${API}/ai-analytics/goals`,
        {
          metric: newGoal.metric,
          target_value: parseFloat(newGoal.target_value),
          target_type: newGoal.target_type || "absolute",
          period: newGoal.period,
        },
        { params }
      );
      
      // Check if this is a GSC-related goal that needs the GSC page
      const isGscGoal = ["gsc_top_queries", "gsc_clicks", "gsc_impressions", "gsc_position"].includes(newGoal.metric);
      
      if (isGscGoal) {
        toast.success("Goal created! Redirecting to manage keyword exclusions...");
        setShowCreateDialog(false);
        setSelectedTemplate(null);
        setNewGoal({ metric: "", target_value: "", target_type: "absolute", period: "monthly" });
        // Redirect to GSC page to set up exclusions
        setTimeout(() => navigate("/app/goals/gsc-tracking"), 1000);
      } else {
        toast.success("Goal created! Calculating progress...");
        setShowCreateDialog(false);
        setSelectedTemplate(null);
        setNewGoal({ metric: "", target_value: "", target_type: "absolute", period: "monthly" });
        fetchGoals(true); // Show refresh toast
      }
    } catch (error) {
      console.error("Error creating goal:", error);
      toast.error(error.response?.data?.detail || "Failed to create goal");
    } finally {
      setCreating(false);
    }
  };

  // Delete goal
  const handleDeleteGoal = async (goalId) => {
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      await axios.delete(`${API}/ai-analytics/goals/${goalId}`, { params });
      toast.success("Goal deleted");
      fetchGoals();
    } catch (error) {
      console.error("Error deleting goal:", error);
      toast.error("Failed to delete goal");
    }
  };

  // Update goal status
  const handleUpdateStatus = async (goalId, newStatus) => {
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      await axios.patch(
        `${API}/ai-analytics/goals/${goalId}`,
        { status: newStatus },
        { params }
      );
      toast.success(`Goal ${newStatus}`);
      fetchGoals();
    } catch (error) {
      console.error("Error updating goal:", error);
      toast.error("Failed to update goal");
    }
  };

  // Calculate progress percentage
  const calcProgress = (current, target) => {
    if (!target || target === 0) return 0;
    return Math.min(Math.round((current / target) * 100), 100);
  };

  // Get status badge
  const getStatusBadge = (goal) => {
    const progress = calcProgress(goal.current_value, goal.target_value);
    
    if (goal.status === "completed") {
      return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
    }
    if (goal.status === "paused") {
      return <Badge className="bg-slate-100 text-slate-600">Paused</Badge>;
    }
    if (goal.status === "pending_approval") {
      return <Badge className="bg-amber-100 text-amber-700">Pending Approval</Badge>;
    }
    if (progress >= 100) {
      return <Badge className="bg-green-100 text-green-700">On Track</Badge>;
    }
    if (progress >= 75) {
      return <Badge className="bg-blue-100 text-blue-700">On Track</Badge>;
    }
    if (progress >= 50) {
      return <Badge className="bg-amber-100 text-amber-700">At Risk</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700">Behind</Badge>;
  };

  // Filter goals by status
  const filteredGoals = goals.filter(g => {
    if (activeTab === "active") return g.status === "active";
    if (activeTab === "pending") return g.status === "pending_approval";
    if (activeTab === "completed") return g.status === "completed";
    if (activeTab === "paused") return g.status === "paused";
    return true;
  });

  // Check if user can manage goals (admin or super admin)
  const canManageGoals = isAdmin || isSuperAdmin;

  if (!canManageGoals) {
    return (
      <div className="animate-fade-in p-6" data-testid="goals-page">
        <Card className="max-w-lg mx-auto mt-20">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h2 className="text-lg font-semibold mb-2">Access Restricted</h2>
            <p className="text-slate-600">
              Goals management is only available to tenant administrators.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-4 sm:space-y-6" data-testid="goals-page">
      {/* Development Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          <span className="font-medium">Goal Creation is in active development.</span> Please consult with JCI prior to creating any goals on your own to verify if the goal creation is active yet or not.
        </p>
      </div>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Marketing Goals</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Set, track, and analyze your marketing goals
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => fetchGoals(true)}
            disabled={loading}
            title="Refresh all goal progress"
            data-testid="refresh-goals-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {isEnabled("create_goals") && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Create Goal</span>
                  <span className="sm:hidden">New</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Goal</DialogTitle>
              <DialogDescription>
                Choose from the Goal Library or create a custom goal
              </DialogDescription>
            </DialogHeader>
            
            {!selectedTemplate ? (
              <div className="py-4">
                <h3 className="text-sm font-medium mb-3">Goal Library</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-2">
                  {GOAL_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template);
                        // Auto-set period to quarterly for GSC goals
                        const newPeriod = template.quarterlyOnly ? "quarterly" : newGoal.period;
                        setNewGoal(prev => ({ ...prev, metric: template.id, period: newPeriod }));
                      }}
                      className="flex items-start gap-3 p-3 border rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-colors text-left"
                    >
                      <div className={`p-2 rounded-lg flex-shrink-0 ${template.bgColor}`}>
                        <template.icon className={`w-5 h-5 ${template.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{template.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{template.description}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {template.isCombined && (
                            <Badge variant="outline" className="text-xs">
                              Combined
                            </Badge>
                          )}
                          {template.drillDown?.enabled && (
                            <Badge variant="outline" className="text-xs bg-violet-50">
                              Drill-down
                            </Badge>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 hidden sm:block" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-4 space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`p-2 rounded-lg ${selectedTemplate.bgColor}`}>
                    <selectedTemplate.icon className={`w-5 h-5 ${selectedTemplate.color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{selectedTemplate.name}</p>
                    <p className="text-xs text-slate-500">{selectedTemplate.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTemplate(null)}
                    className="ml-auto"
                  >
                    Change
                  </Button>
                </div>
                
                {/* Show breakdown for combined metrics */}
                {selectedTemplate.breakdown && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-xs font-medium text-emerald-800 mb-2">This goal combines:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.breakdown.map((item) => (
                        <div key={item.id} className="flex items-center gap-1.5 px-2 py-1 bg-white rounded border text-xs">
                          <item.icon className="w-3 h-3 text-emerald-600" />
                          {item.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Show drill-down info for dwell time */}
                {selectedTemplate.drillDown?.enabled && (
                  <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                    <p className="text-xs font-medium text-violet-800 mb-1">Drill-down available:</p>
                    <p className="text-xs text-violet-600">{selectedTemplate.drillDown.description}</p>
                  </div>
                )}
                
                {/* Data Source Note */}
                {selectedTemplate.dataSource && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-xs text-slate-600">
                      <span className="font-medium">Data Source:</span> {selectedTemplate.dataSource}
                      {selectedTemplate.note && <span className="block mt-1 text-slate-500">{selectedTemplate.note}</span>}
                    </p>
                  </div>
                )}
                
                {/* Baseline Data Display */}
                {loadingBaseline ? (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                      <span className="text-sm text-blue-700">Loading historical baseline...</span>
                    </div>
                  </div>
                ) : baselineData?.baseline_value ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-green-800">Historical Baseline ({newGoal.period})</p>
                        <p className="text-lg font-bold text-green-700">
                          {baselineData.baseline_value.toLocaleString()}
                          {selectedTemplate.unit === "percent" && "%"}
                          {selectedTemplate.unit === "seconds" && " sec"}
                          {selectedTemplate.unit === "hours" && " hrs"}
                          {selectedTemplate.unit === "days" && " days"}
                        </p>
                        <p className="text-xs text-green-600">
                          Based on {baselineData.data_points || 0} data points from {baselineData.period_label || "prior period"}
                        </p>
                      </div>
                      {baselineData.trend && (
                        <div className={`text-right ${baselineData.trend > 0 ? 'text-green-700' : 'text-red-600'}`}>
                          <p className="text-xs">Recent trend</p>
                          <p className="font-medium">{baselineData.trend > 0 ? '+' : ''}{baselineData.trend}%</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-700">
                        No historical data available for this metric yet
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Target Type Selection (for templates that support it) */}
                {selectedTemplate.supportsPercentChange && (
                  <div className="space-y-2">
                    <Label>Target Type</Label>
                    <Select
                      value={newGoal.target_type}
                      onValueChange={(value) => setNewGoal(prev => ({ ...prev, target_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TARGET_TYPE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex flex-col">
                              <span>{opt.label}</span>
                              <span className="text-xs text-slate-500">{opt.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      {newGoal.target_type === "percent_increase" 
                        ? "Growth % (e.g., 15 = +15%)"
                        : newGoal.target_type === "percent_decrease"
                        ? "Reduction % (e.g., 10 = -10%)"
                        : `Target Value ${selectedTemplate.unit === "seconds" ? "(sec)" : selectedTemplate.unit === "percent" ? "(%)" : ""}`
                      }
                    </Label>
                    <Input
                      type="number"
                      placeholder={
                        newGoal.target_type !== "absolute" 
                          ? "e.g., 15"
                          : selectedTemplate.unit === "seconds" 
                          ? "e.g., 180" 
                          : selectedTemplate.unit === "percent"
                          ? "e.g., 25"
                          : "e.g., 100"
                      }
                      value={newGoal.target_value}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, target_value: e.target.value }))}
                    />
                    
                    {/* Show calculated actual target for percent changes */}
                    {newGoal.target_type !== "absolute" && baselineData?.baseline_value && newGoal.target_value && (
                      <div className="p-2 bg-indigo-50 border border-indigo-200 rounded mt-2">
                        <p className="text-xs text-indigo-600">
                          <span className="font-medium">Actual Target: </span>
                          <span className="text-indigo-800 font-bold">
                            {calculateActualTarget()?.toLocaleString()}
                            {selectedTemplate.unit === "percent" && "%"}
                          </span>
                          <span className="text-indigo-500 ml-1">
                            ({newGoal.target_type === "percent_increase" ? '+' : '-'}{newGoal.target_value}% from {baselineData.baseline_value.toLocaleString()})
                          </span>
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500 hidden sm:block">
                      {newGoal.target_type === "percent_increase"
                        ? `Enter the growth percentage you want to achieve`
                        : newGoal.target_type === "percent_decrease"
                        ? `Enter the reduction percentage (for cost metrics)`
                        : selectedTemplate.unit === "seconds" 
                        ? "180 sec = 3 min"
                        : selectedTemplate.lowerIsBetter
                        ? "Lower is better"
                        : "Based on historical performance"
                      }
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Period</Label>
                    {selectedTemplate?.quarterlyOnly ? (
                      <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                        <Clock className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-700">Quarterly (required for SEO goals)</span>
                      </div>
                    ) : (
                      <Select
                        value={newGoal.period}
                        onValueChange={(value) => setNewGoal(prev => ({ ...prev, period: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PERIOD_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                
                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    Goals are validated against historical data. Targets over 1.5x max may need admin approval.
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => {
                setShowCreateDialog(false);
                setSelectedTemplate(null);
              }} className="w-full sm:w-auto">
                Cancel
              </Button>
              {selectedTemplate && (
                <Button onClick={handleCreateGoal} disabled={creating || !newGoal.target_value} className="w-full sm:w-auto">
                  {creating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Goal
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Goals</p>
                <p className="text-2xl font-bold">{goals.filter(g => g.status === "active").length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">On Track</p>
                <p className="text-2xl font-bold text-green-600">
                  {goals.filter(g => g.status === "active" && calcProgress(g.current_value, g.target_value) >= 75).length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">At Risk</p>
                <p className="text-2xl font-bold text-amber-600">
                  {goals.filter(g => g.status === "active" && calcProgress(g.current_value, g.target_value) < 75 && calcProgress(g.current_value, g.target_value) >= 50).length}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-slate-600">
                  {goals.filter(g => g.status === "completed").length}
                </p>
              </div>
              <div className="p-3 bg-slate-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Goals List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>Goals</CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="pending" className="relative">
                  Pending
                  {goals.filter(g => g.status === "pending_approval").length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                      {goals.filter(g => g.status === "pending_approval").length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
                <TabsTrigger value="paused">Paused</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filteredGoals.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No {activeTab !== "all" ? activeTab : ""} goals yet</p>
              <p className="text-sm text-slate-400 mt-1">Create your first goal to start tracking</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {filteredGoals.map((goal) => {
                const progress = calcProgress(goal.current_value, goal.target_value);
                const template = GOAL_TEMPLATES.find(t => t.id === goal.metric);
                const IconComponent = template?.icon || Target;
                
                return (
                  <div
                    key={goal.goal_id}
                    className="border rounded-lg p-3 sm:p-4 hover:border-slate-300 transition-colors"
                    data-testid={`goal-${goal.goal_id}`}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${template?.bgColor || "bg-slate-100"}`}>
                        <IconComponent className={`w-4 h-4 sm:w-5 sm:h-5 ${template?.color || "text-slate-600"}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-medium text-sm sm:text-base">
                            {template?.name || goal.metric}
                          </h3>
                          {getStatusBadge(goal)}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            {goal.period}
                          </span>
                          <span className="hidden sm:inline">
                            {new Date(goal.period_start).toLocaleDateString()} - {new Date(goal.period_end).toLocaleDateString()}
                          </span>
                          {/* Data source indicator for website_sessions */}
                          {goal.metric === "website_sessions" && goal.data_source && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                goal.data_source === "hubspot" ? "border-orange-300 text-orange-700 bg-orange-50" :
                                goal.data_source === "ga4" ? "border-blue-300 text-blue-700 bg-blue-50" :
                                "border-slate-300 text-slate-500"
                              }`}
                            >
                              {goal.data_source === "hubspot" ? "HubSpot" : 
                               goal.data_source === "ga4" ? "GA4" : 
                               goal.data_source === "demo" ? "Demo" : "N/A"}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span>{goal.current_value.toLocaleString()} / {goal.target_value.toLocaleString()}</span>
                            <span className="font-medium">{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-1.5 sm:h-2" />
                          
                          {/* Show "No Change" indicator when current equals baseline */}
                          {goal.baseline_value !== undefined && goal.current_value === goal.baseline_value && (
                            <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                              <span className="inline-block w-2 h-2 rounded-full bg-slate-400" />
                              No change from baseline ({goal.baseline_value.toLocaleString()})
                            </div>
                          )}
                          
                          {/* Show baseline info for percent-based goals */}
                          {goal.target_type !== "absolute" && goal.baseline_value !== undefined && (
                            <div className="text-xs text-slate-500 mt-1">
                              <span className="inline-flex items-center gap-1">
                                {goal.target_type === "percent_increase" ? (
                                  <TrendingUp className="w-3 h-3 text-green-500" />
                                ) : (
                                  <TrendingDown className="w-3 h-3 text-red-500" />
                                )}
                                {goal.target_percent}% {goal.target_type === "percent_increase" ? "growth" : "reduction"} from baseline of {goal.baseline_value.toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {goal.validation?.requires_admin_approval && !goal.admin_approved && (
                          <div className="flex items-center gap-2 mt-3 p-2 bg-amber-50 rounded text-xs text-amber-700">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="line-clamp-1">Requires admin approval</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                        {goal.status === "active" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUpdateStatus(goal.goal_id, "paused")}
                            title="Pause goal"
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        )}
                        {goal.status === "paused" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUpdateStatus(goal.goal_id, "active")}
                            title="Resume goal"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteGoal(goal.goal_id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Delete goal"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Insights Panel - Shows for at-risk goals */}
      {goals.filter(g => g.status === "active" && calcProgress(g.current_value, g.target_value) < 75).map(atRiskGoal => {
        const goalId = atRiskGoal.goal_id || atRiskGoal.id;
        const insight = aiInsights[goalId];
        const isLoading = loadingInsights[goalId];
        
        // Only show approved insights to users
        const hasApprovedInsight = insight?.status === "approved";
        
        return (
          <Card key={`insight-${goalId}`} className="border-amber-200 bg-amber-50/50" data-testid={`ai-insight-${goalId}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Brain className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium flex items-center gap-2 mb-1">
                    AI Insights: {atRiskGoal.name}
                    <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                      {Math.round(calcProgress(atRiskGoal.current_value, atRiskGoal.target_value))}% of target
                    </Badge>
                  </h3>
                  {insight?.generated_at && (
                    <p className="text-xs text-slate-400 mb-4">
                      Generated {new Date(insight.generated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  )}
                  
                  {isLoading && (
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Loading insights...</span>
                    </div>
                  )}
                  
                  {/* Human-in-the-loop message when no approved insight */}
                  {!hasApprovedInsight && !isLoading && (
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <p className="text-sm text-slate-600">
                        This goal has been flagged as <span className="font-medium text-amber-700">at risk</span> based on pre-defined performance thresholds.
                      </p>
                      <p className="text-sm text-slate-500 mt-2">
                        AI-powered insights are generated automatically and reviewed by our team before being displayed here. 
                        This ensures accuracy and actionable recommendations tailored to your specific situation.
                      </p>
                      <p className="text-xs text-slate-400 mt-3 italic">
                        Insights will appear here once validated by the JCI team.
                      </p>
                    </div>
                  )}
                  
                  {hasApprovedInsight && !isLoading && (
                    <div className="space-y-3">
                      {/* Stats Summary */}
                      {insight.stats && (
                        <div className="flex flex-wrap gap-4 text-xs text-slate-500 pb-3 border-b border-amber-200">
                          <span>{insight.stats.baseline_label || "Baseline"}: {insight.stats.baseline_keywords?.toLocaleString()} keywords</span>
                          <span>Current: {insight.stats.current_keywords?.toLocaleString()} keywords</span>
                          <span className={insight.stats.decline_pct <= 0 ? "text-red-600" : "text-green-600"}>
                            Change: {insight.stats.decline_pct > 0 ? "+" : ""}{insight.stats.decline_pct}%
                          </span>
                        </div>
                      )}
                      
                      {/* Page Volatility Breakdown */}
                      {insight.stats?.page_volatility && (
                        <div className="bg-white rounded-lg p-3 border border-amber-200">
                          <h4 className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            Page-Level Ranking Volatility
                          </h4>
                          <div className="grid grid-cols-2 gap-3">
                            {/* Content Pages */}
                            <div className="p-2 bg-green-50 rounded border border-green-200">
                              <div className="text-xs font-medium text-green-800">Content Pages</div>
                              <div className="text-xs text-green-600">Blogs, webinars, news</div>
                              <div className="mt-1 text-sm font-semibold text-green-700">
                                {Math.abs(insight.stats.page_volatility.content_pages?.pct_of_loss || 0).toFixed(0)}% of ranking losses
                              </div>
                              <div className="text-xs text-green-600">
                                {insight.stats.page_volatility.content_pages?.pages || 0} pages affected
                              </div>
                              <div className="text-[10px] text-green-500 mt-1 italic">
                                Within normal market fluctuation
                              </div>
                            </div>
                            {/* Core Pages */}
                            <div className="p-2 bg-amber-50 rounded border border-amber-200">
                              <div className="text-xs font-medium text-amber-800">Core Site Pages</div>
                              <div className="text-xs text-amber-600">Homepage, solutions, team</div>
                              <div className="mt-1 text-sm font-semibold text-amber-700">
                                {Math.abs(insight.stats.page_volatility.core_pages?.pct_of_loss || 0).toFixed(0)}% of ranking losses
                              </div>
                              <div className="text-xs text-amber-600">
                                {insight.stats.page_volatility.core_pages?.pages || 0} pages affected
                              </div>
                              <div className="text-[10px] text-amber-500 mt-1 italic">
                                {(insight.stats.page_volatility.core_pages?.pct_of_loss || 0) > 50 
                                  ? "Content re-optimization recommended" 
                                  : "Within expected range"}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* AI-generated insight text - render markdown */}
                      <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none">
                        <ReactMarkdown
                          components={{
                            h1: ({node, ...props}) => <h1 className="text-lg font-bold text-purple-900 mb-2" {...props} />,
                            h2: ({node, ...props}) => <h2 className="text-base font-semibold text-purple-800 mb-2 mt-3" {...props} />,
                            h3: ({node, ...props}) => <h3 className="text-sm font-semibold text-slate-700 mb-1 mt-2" {...props} />,
                            p: ({node, ...props}) => <p className="text-sm text-slate-600 mb-2" {...props} />,
                            strong: ({node, ...props}) => <strong className="font-semibold text-slate-800" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc pl-4 mb-2 space-y-1" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...props} />,
                            li: ({node, ...props}) => <li className="text-sm text-slate-600" {...props} />,
                            blockquote: ({node, ...props}) => <blockquote className="border-l-3 border-amber-400 pl-3 italic text-amber-700 bg-amber-50/50 py-1 my-2 rounded-r" {...props} />,
                          }}
                        >
                          {insight.insight_text || insight.insight || ""}
                        </ReactMarkdown>
                      </div>
                      
                      {/* Sources */}
                      {insight.sources && insight.sources.length > 0 && (
                        <div className="pt-2 border-t border-amber-200">
                          <p className="text-xs text-slate-500 mb-1">Sources:</p>
                          <div className="flex flex-wrap gap-2">
                            {insight.sources.map((source, idx) => (
                              <a
                                key={idx}
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-amber-700 hover:text-amber-800 underline"
                              >
                                {source.title || (source.url ? new URL(source.url).hostname : "Source")}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default GoalsPage;
